import {createReducer} from "redux-act";
import {
	fetchFanFactsJSONSuccess,
	fetchFanSentimentsJsonSuccess,
	fetchQualifierGroupsJSON,
	fetchQualifierGroupsJSONSuccess,
	fetchSettingsJSON,
	fetchSettingsJSONFailed,
	fetchSettingsJSONSuccess,
	fetchSupportedCountriesJsonSuccess,
	fetchQualifierGroupsJSONFailed,
	fetchQualifierSquadsJSON,
	fetchQualifierSquadsJSONSuccess,
	fetchQualifierSquadsJSONFailed,
} from "apps/DRAW_PREDICTOR/modules/actions";
import {IDictionary} from "modules/types";
import {Confederation, FanDrawType, PreDrawGroupType} from "apps/DRAW_PREDICTOR/modules/enums";
import {RequestState} from "modules";

export interface IFanSentiments {
	pre_draw: IFanDraw[];
	pre_draw_qual_teams: IFanDraw[];
	post_draw: IFanDraw[];
	post_draw_qual_teams: IFanDraw[];
}

export interface IFanDraw {
	id: number;
	type: FanDrawType;
	question: string;
	answerType: FanDrawType;
	answerNumber: number;
	options: IFanDrawOption[] | null;
	playerOptions: {[key: string]: IFanDrawOption[]} | null;
	section: string;
}

export interface IFanDrawOption {
	id: number;
	text: string;
}

export interface IDrawPredictorSettings {
	lockoutDate: string;
	isLocked: boolean;
	countdownTimer: string;
}

export type IFanFact = string[];

export interface IQualifierGroup {
	id: number;
	name: IQualifierGroupDescription;
	description: IQualifierGroupDescription;
	questions: IQualifierGroupQuestion[];
	type: null | PreDrawGroupType;
	pre_state_enabled: boolean;
}

export interface IQualifierGroupDescription {
	ar: string;
	de: string;
	en: string;
	es: string;
	fr: string;
}

export interface IQualifierGroupQuestion {
	id: number;
	options: IQualifierGroupOption[];
}

export interface IQualifierGroupOption {
	id: number;
	squadId: number;
}

export interface IQualifierSquad {
	id: number;
	name: string;
	shortName: string;
	confederation: Confederation;
	rank: number;
	is_qualified: boolean;
	pot: number;
}

export interface IJsonsReducer {
	supportedCountries: IDictionary<string>;
	fanSentiments: IFanSentiments;
	settings: IDrawPredictorSettings;
	fanFacts: IDictionary<IFanFact>;
	qualifierGroups: IQualifierGroup[];
	qualifierSquads: IQualifierSquad[];
	requests: {
		settings: RequestState;
		qualifierGroups: RequestState;
		qualifierSquads: RequestState;
	};
}

const initial: IJsonsReducer = {
	supportedCountries: {},
	fanSentiments: {
		pre_draw: [],
		pre_draw_qual_teams: [],
		post_draw: [],
		post_draw_qual_teams: [],
	},
	settings: {
		lockoutDate: "",
		countdownTimer: "",
		isLocked: false,
	},
	fanFacts: {},
	qualifierGroups: [],
	qualifierSquads: [],
	requests: {
		settings: RequestState.IDLE,
		qualifierGroups: RequestState.IDLE,
		qualifierSquads: RequestState.IDLE,
	},
};

export const projectJsons = createReducer<IJsonsReducer>({}, initial)
	.on(fetchSupportedCountriesJsonSuccess, (state, payload) => ({
		...state,
		supportedCountries: payload,
	}))
	.on(fetchFanSentimentsJsonSuccess, (state, payload) => ({
		...state,
		fanSentiments: payload,
	}))
	.on(fetchSettingsJSON, (state) => ({
		...state,
		requests: {
			...state.requests,
			settings: RequestState.Requested,
		},
	}))
	.on(fetchSettingsJSONSuccess, (state, payload) => ({
		...state,
		settings: payload,
		requests: {
			...state.requests,
			settings: RequestState.Received,
		},
	}))
	.on(fetchSettingsJSONFailed, (state) => ({
		...state,
		requests: {
			...state.requests,
			settings: RequestState.Received,
		},
	}))
	.on(fetchFanFactsJSONSuccess, (state, payload) => ({
		...state,
		fanFacts: payload,
	}))
	.on(fetchQualifierGroupsJSON, (state) => ({
		...state,
		requests: {
			...state.requests,
			qualifierGroups: RequestState.Requested,
		},
	}))
	.on(fetchQualifierGroupsJSONSuccess, (state, payload) => ({
		...state,
		qualifierGroups: payload,
		requests: {
			...state.requests,
			qualifierGroups: RequestState.Received,
		},
	}))
	.on(fetchQualifierGroupsJSONFailed, (state) => ({
		...state,
		requests: {
			...state.requests,
			qualifierGroups: RequestState.Received,
		},
	}))
	.on(fetchQualifierSquadsJSON, (state) => ({
		...state,
		requests: {
			...state.requests,
			qualifierSquads: RequestState.Requested,
		},
	}))
	.on(fetchQualifierSquadsJSONSuccess, (state, payload) => ({
		...state,
		qualifierSquads: payload,
		requests: {
			...state.requests,
			qualifierSquads: RequestState.Received,
		},
	}))
	.on(fetchQualifierSquadsJSONFailed, (state) => ({
		...state,
		requests: {
			...state.requests,
			qualifierSquads: RequestState.Received,
		},
	}));
